import { useState, useEffect } from "react";
import { FiSearch, FiGrid, FiPlus, FiTruck, FiCopy } from "react-icons/fi";
import { BiSearch } from "react-icons/bi";
import { IoCloseSharp } from "react-icons/io5";
import GeneralButton from "../../components/button/GeneralButton";
import FormInput from "../../components/formInput/FormInput";
import AddNewVendorDetails from "../../components/modals/AddNewVendorDetails";
import MergeVendorsModal from "../../components/modals/MergeVendorsModal";
import VendorsTable from "../../components/table/VendorsTable";
import useVendors from "./customHooks/useVendors";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
const Vendors = () => {
  const {
    modal,
    toggleCreateVendor,
    mergeModal,
    toggleMergeModal,
    vendors,
    setSelectedData,
    selectedRows,
    setSelectedRows,
    searchValue,
    setSearchValue,
    gettingVendors,
    data,
    setData,
    page,
    setPage,
    pageSize,
    setPageSize,
    loading,
    totalPages
  } = useVendors();

  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const history = localStorage.getItem("vendorSearchHistory");
      if (!history) return [];
      const parsed = JSON.parse(history);
      const now = Date.now();
      return parsed.filter(item => item.timestamp && (now - item.timestamp < 3600000));
    } catch (e) {
      return [];
    }
  });
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setSearchHistory(prev => {
        const now = Date.now();
        const filtered = prev.filter(item => item.timestamp && (now - item.timestamp < 3600000));
        if (filtered.length !== prev.length) {
          localStorage.setItem("vendorSearchHistory", JSON.stringify(filtered));
        }
        return filtered;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const clearSearch = () => {
    setSearchValue("");
    localStorage.removeItem("vendorSearch");
    setPage(1);
  };

  const saveToHistory = (term) => {
    const cleanTerm = term?.trim();
    if (!cleanTerm || cleanTerm.length < 2) return;
    setSearchHistory((prev) => {
      const currentHistory = Array.isArray(prev) ? prev : [];
      const now = Date.now();
      const newHistory = [
        { term: cleanTerm, timestamp: now },
        ...currentHistory.filter((item) => item.term !== cleanTerm)
      ].slice(0, 10);
      localStorage.setItem("vendorSearchHistory", JSON.stringify(newHistory));
      return newHistory;
    });
  };

  useEffect(() => {
    if (searchValue && searchValue.trim().length >= 3) {
      const timer = setTimeout(() => {
        saveToHistory(searchValue);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [searchValue]);

  const handleHistoryClick = (term) => {
    setSearchValue(term);
    setPage(1);
    localStorage.setItem("vendorSearch", term);
    setShowHistory(false);
    saveToHistory(term);
  };

  const clearHistory = (e) => {
    e.stopPropagation();
    setSearchHistory([]);
    localStorage.removeItem("vendorSearchHistory");
  };

  return (
    <div className="settings-container">
      <AddNewVendorDetails
        modal={modal}
        toggle={() => {
          toggleCreateVendor();
        }}
        data={data}
        setData={setData}
        gettingVendors={gettingVendors}
      />

      <MergeVendorsModal
        isOpen={mergeModal}
        toggle={toggleMergeModal}
        gettingVendors={gettingVendors}
      />

      <div className="px-4">
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center ">
          <div className="d-flex align-items-center gap-3 flex-1">
            <div className="bg-primary-soft p-3 rounded-circle shadow-sm">
              <FiTruck size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="fw-bold text-dark mb-0">Vendors</h4>
              <p className="text-muted small mb-0 mt-1">Records ({totalPages ?? 0} Total)</p>
            </div>
          </div>

          <div className="flex-1 d-flex search-input-wrapper ">
            <div className="position-relative" style={{ width: "400px" }}>
              <FormInput
                type={"text"}
                placeholder={"Search vendor..."}
                size={'large'}
                className="border-0 shadow-none"
                icon={<FiSearch size={"20"} className="text-muted" />}
                value={searchValue}
                handleChange={(e) => {
                  setPage(1)
                  localStorage.setItem('vendorSearch', e.target.value);
                  setSearchValue(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    saveToHistory(searchValue);
                    setShowHistory(false);
                  }
                }}
                onFocus={() => setShowHistory(true)}
                onBlur={() => setTimeout(() => setShowHistory(false), 200)}
                suffix={
                  searchValue ? (
                    <IoCloseSharp
                      size={18}
                      className="text-muted pointer"
                      onClick={clearSearch}
                    />
                  ) : null
                }
              />
              {showHistory && searchHistory.length > 0 && (
                <div
                  className="position-absolute w-100 bg-white border shadow-lg mt-1"
                  style={{
                    zIndex: 2000,
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    top: '100%',
                    left: 0
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light sticky-top">
                    <span className="text-muted small fw-bold">RECENT SEARCHES</span>
                    <span
                      className="text-primary small cursor-pointer fw-bold"
                      onClick={clearHistory}
                      style={{ cursor: 'pointer' }}
                    >
                      Clear All
                    </span>
                  </div>
                  {searchHistory.map((item, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center p-2 border-bottom"
                      style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      onClick={() => handleHistoryClick(item.term)}
                    >
                      <BiSearch size={16} className="text-muted me-2" />
                      <span className="flex-grow-1" style={{ fontSize: '14px', color: '#333' }}>{item.term}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="d-flex gap-3 flex-1 justify-content-end align-items-center">
            <GeneralButton
              title={"Merge Vendor"}
              className={"red-button shadow-sm d-inline-flex align-items-center justify-content-center m-0"}
              height={"42px"}
              width={"200px"}
              handleClick={toggleMergeModal}
              icon={<FiCopy />}
            />
            <GeneralButton
              title={"Create Vendor"}
              className={"create-user-btn text-white shadow-sm d-inline-flex align-items-center justify-content-center m-0"}
              height={"42px"}
              width={"200px"}
              handleClick={toggleCreateVendor}
              icon={<FiPlus />}
            />
          </div>
        </div>

        <div className="settings-card">
          {loading ? (
            <TableSkeleton columns={7} />
          ) : (
            <VendorsTable
              vendors={vendors}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              setSelectedData={setSelectedData}
              searchValue={searchValue}
              page={page}
              totalPages={totalPages}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              gettingVendors={gettingVendors}
              onClearSearch={searchValue ? clearSearch : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Vendors;
